import { Layout } from "antd";
import { HTMLProps, useEffect } from "react";
import DocumentTitle from "react-document-title";
import { Link, useNavigate } from "react-router";
import { RoutePaths } from "../routes";
import { HTMLMotionProps, motion } from "framer-motion";
import Button from "../Components/Button";
import { useCurrentUser } from "../../data/hooks";
import Spin from "../Components/Spin";

const { Content } = Layout;

function MotionEnterUp(props: HTMLMotionProps<"div">) {
  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      transition={{ ease: "easeOut", duration: 0.5 }}
      viewport={{ once: true }}
      {...props}
    />
  );
}

function MotionRotateX(props: HTMLMotionProps<"div">) {
  return (
    <motion.div
      initial={{ transform: "perspective(1000px) rotateX(30deg)" }}
      whileInView={{ transform: "perspective(2000px) rotateX(2deg)" }}
      transition={{ ease: "easeOut", duration: 0.5 }}
      viewport={{ once: true }}
      {...props}
    />
  );
}

function GetStartedButton() {
  return (
    <Link to={RoutePaths.registration} viewTransition>
      <Button shadow>Get started</Button>
    </Link>
  );
}

function Img(props: HTMLProps<HTMLImageElement>) {
  return (
    <img
      className="h-52 w-full rounded-3xl object-cover md:h-full"
      {...props}
    />
  );
}

function Subtitle(props: HTMLProps<HTMLSpanElement>) {
  return (
    <span
      {...props}
      className={
        "text-3xl font-bold md:text-4xl lg:text-5xl" + " " + props.className
      }
    />
  );
}

function Text(props: HTMLProps<HTMLSpanElement>) {
  return (
    <span
      {...props}
      className="text-lg md:text-xl framer-body-gray font-medium lg:text-2xl"
    />
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user, authStateLoaded } = useCurrentUser();

  useEffect(() => {
    if (user && authStateLoaded) {
      console.log("Landing: redirecting signed in user", user);
      navigate(RoutePaths.myDefaultWishlist);
    }
  }, [user, authStateLoaded]);

  if (!authStateLoaded) {
    return <Spin />;
  }

  return (
    <>
      <DocumentTitle title="Wishlist - welcome">
        <>
          <Content data-testid="landing">
            <div className="flex flex-col items-center">
              <div className="flex max-w-screen-xl flex-col-reverse gap-4 p-8 md:flex-row md:py-20">
                <MotionEnterUp className="flex flex-col items-center justify-center gap-5">
                  <MotionEnterUp className="text-4xl font-bold md:text-6xl lg:text-7xl">
                    Wishlist
                  </MotionEnterUp>
                  <Subtitle className="text-center">
                    Redefining the Joy of Gift-Giving
                  </Subtitle>
                  <span className="text-base framer-body-gray text-center font-medium">
                    Reimagine the joy of giving and receiving gifts with our
                    wishlist, built to surprise while offering unmatched
                    convenience.
                  </span>
                  <GetStartedButton />
                </MotionEnterUp>
                <div>
                  <MotionRotateX>
                    <Img src="placeholder.webp" />
                  </MotionRotateX>
                </div>
              </div>
              <div className="framer-bg-gray flex w-full flex-col items-center gap-10 px-6 py-16 md:px-12 md:py-10 lg:px-3 lg:py-24">
                <MotionEnterUp className="flex max-w-screen-xl flex-col gap-12 md:flex-row">
                  <Img src="landing1.webp" />
                  <div className="flex flex-col justify-center gap-5">
                    <Subtitle>Build Your Wishlist</Subtitle>
                    <Text>
                      Create a list of what you truly want, making it easy for
                      friends to find the perfect gift for you.
                    </Text>
                  </div>
                </MotionEnterUp>
                <MotionEnterUp className="flex max-w-screen-xl flex-col-reverse gap-12 md:flex-row">
                  <div className="flex flex-col justify-center gap-5">
                    <Subtitle>Connect with Friends</Subtitle>
                    <Text>
                      Invite friends to choose gifts for you and create their
                      own wishlists for fun, mutual surprises.
                    </Text>
                  </div>
                  <Img src="landing2.webp" />
                </MotionEnterUp>
                <MotionEnterUp className="flex max-w-screen-xl flex-col gap-12 md:flex-row">
                  <Img src="landing3.webp" />
                  <div className="flex flex-col justify-center gap-5">
                    <Subtitle>Choose, Reserve &amp; Give Gifts</Subtitle>
                    <Text>
                      Pick a gift, reserve it, and enjoy the joy of giving
                      something truly desired.
                    </Text>
                  </div>
                </MotionEnterUp>
              </div>

              <div className="framer-bg-gray-lighter flex w-full flex-col items-center gap-7 px-10 py-24">
                <Subtitle>Sign up today.</Subtitle>
                <GetStartedButton />
              </div>

              <div className="framer-bg-dark-gray framer-color-light flex w-full flex-col items-center gap-3 p-7 md:flex-row md:justify-between md:p-12">
                <div>© Wishlist 2024</div>
                <div className="flex flex-row gap-4">
                  <Link to={RoutePaths.contactUs} viewTransition>
                    Contact Us
                  </Link>
                  <Link to={RoutePaths.privacyPolicy} viewTransition>
                    Privacy Policy
                  </Link>
                  <Link to={RoutePaths.faq} viewTransition>
                    FAQ
                  </Link>
                </div>
              </div>
            </div>
          </Content>
        </>
      </DocumentTitle>
    </>
  );
}
